import axios from "axios";

import * as api from "../../types/api";

const baseUrl = () => `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1`;

const headers = (token: string) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Admin-Request": "true",
});

export interface IIngredientListParameters {
    q?: string;
    categoryId?: string;
    status?: api.IngredientStatus;
    page: number;
    limit: number;
}

export type TIngredientPayload = Partial<
    Pick<api.IIngredient, "name" | "aliases" | "categoryId" | "baseUnit" | "gramsPerPiece" | "isStaple" | "status">
>;

export type TIngredientCategoryPayload = Partial<Pick<api.IIngredientCategory, "name" | "icon" | "position">>;

export type TBulkIngredientPayload = Omit<TIngredientPayload, "categoryId"> & {
    name: string;
    categorySlug?: string | null;
};

export class Ingredients {
    public static async listCategories(token: string): Promise<api.IIngredientCategory[]> {
        const { data } = await axios.get(`${baseUrl()}/ingredient-categories`, { headers: headers(token) });
        return data;
    }

    public static async createCategory(
        body: TIngredientCategoryPayload,
        token: string,
    ): Promise<api.IIngredientCategory> {
        const { data } = await axios.post(`${baseUrl()}/ingredient-categories`, body, { headers: headers(token) });
        return data;
    }

    public static async updateCategory(
        categoryId: string,
        body: TIngredientCategoryPayload,
        token: string,
    ): Promise<api.IIngredientCategory> {
        const { data } = await axios.patch(`${baseUrl()}/ingredient-categories/${categoryId}`, body, {
            headers: headers(token),
        });
        return data;
    }

    public static async deleteCategory(categoryId: string, token: string): Promise<void> {
        await axios.delete(`${baseUrl()}/ingredient-categories/${categoryId}`, { headers: headers(token) });
    }

    public static async list(
        parameters: IIngredientListParameters,
        token: string,
    ): Promise<api.IOffsetPage<api.IIngredient>> {
        const { data } = await axios.get(`${baseUrl()}/ingredients`, {
            headers: headers(token),
            params: parameters,
        });
        return data;
    }

    public static async get(ingredientId: string, token: string): Promise<api.IIngredient> {
        const { data } = await axios.get(`${baseUrl()}/ingredients/${ingredientId}`, { headers: headers(token) });
        return data;
    }

    public static async create(body: TIngredientPayload, token: string): Promise<api.IIngredient> {
        const { data } = await axios.post(`${baseUrl()}/ingredients`, body, { headers: headers(token) });
        return data;
    }

    public static async update(ingredientId: string, body: TIngredientPayload, token: string): Promise<api.IIngredient> {
        const { data } = await axios.patch(`${baseUrl()}/ingredients/${ingredientId}`, body, {
            headers: headers(token),
        });
        return data;
    }

    public static async delete(ingredientId: string, token: string): Promise<void> {
        await axios.delete(`${baseUrl()}/ingredients/${ingredientId}`, { headers: headers(token) });
    }

    public static async bulkImport(
        items: TBulkIngredientPayload[],
        token: string,
    ): Promise<{ created: number; updated: number; skipped: string[] }> {
        const { data } = await axios.post(`${baseUrl()}/ingredients/bulk`, { items }, { headers: headers(token) });
        return data;
    }

    public static async listUnmatched(
        parameters: { page: number; limit: number },
        token: string,
    ): Promise<api.IOffsetPage<api.IUnmatchedIngredientName>> {
        const { data } = await axios.get(`${baseUrl()}/ingredients/unmatched`, {
            headers: headers(token),
            params: parameters,
        });
        return data;
    }

    public static async resolveUnmatched(
        unmatchedId: string,
        body: { ingredientId: string } | { ingredient: TIngredientPayload & { name: string } },
        token: string,
    ): Promise<api.IIngredient> {
        const { data } = await axios.post(`${baseUrl()}/ingredients/unmatched/${unmatchedId}/resolve`, body, {
            headers: headers(token),
        });
        return data;
    }
}

export class ShoppingLists {
    public static async getForCustomer(customerId: string, token: string): Promise<api.IShoppingList | null> {
        const { data } = await axios.get(`${baseUrl()}/customers/${customerId}/shopping-list`, {
            headers: headers(token),
        });
        return data;
    }

    public static async recalculateForCustomer(customerId: string, token: string): Promise<api.IShoppingList> {
        const { data } = await axios.post(
            `${baseUrl()}/customers/${customerId}/shopping-list/recalculate`,
            {},
            { headers: headers(token) },
        );
        return data;
    }
}
